import React, { useEffect, useMemo, useRef, useState } from 'react';

import ReactDOM from 'react-dom';
import { useHistory, matchPath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useInfiniteQuery, useQueryClient } from 'react-query';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import { toggleNotificationsSidebar } from '@redux/actions/appState.actions';
import { appStateSelector } from '@redux/selectors/appState.selectors';
import useOutsideClick from '@hooks/useOutsideClick';
import useHotkeys from '@hooks/useHotkeys';

import './NotificationsSidebar.scss';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import urls from '@constants/urls';
import { NotificationCounterType, NotificationType } from '@ts/userOrganization.types';
import {
  deleteNotificationApi,
  getNotificationsApi,
  markNotificationAsSeenApi,
} from '@api/userNotification.api';

import { GNCConfigItemType } from './gncConfig';

const checkUrl = (pathname, path) =>
  matchPath(pathname, {
    path,
    exact: true,
  });
const openNotificationLink = (link: string) => {
  const { pathname, search } = new URL(link);
  const isLocal = window.location.hostname === 'localhost';

  // set localhost instead of dev.server
  // handle "filters" query param
  if (isLocal && (checkUrl(pathname, urls.PLACEMENTS()) || checkUrl(pathname, urls.ORDERS()))) {
    window.open(window.location.origin + pathname + search, '_blank', 'noopener,noreferrer');
  } else {
    window.open(link, '_blank', 'noopener,noreferrer');
  }
};

function NotificationsSidebar(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const notificationsSidebarRef = useRef<HTMLDivElement>();
  const { isNotificationsSidebarOpen } = useSelector(appStateSelector);
  const queryClient = useQueryClient();

  const [gncNotifications, changeGncNotifications] = useState<GNCConfigItemType[]>([]);

  const currentWorkspaceId = useCurrentWorkspaceId();
  const key = useMemo(
    () => ['workspace-notifications-content', currentWorkspaceId],
    [currentWorkspaceId]
  );

  const { data } = useInfiniteQuery<NotificationType[]>(
    key,
    ({ pageParam = 0 }) => getNotificationsApi(currentWorkspaceId, pageParam, 10),
    {
      getNextPageParam: (lastPage: NotificationType[], allPages) => {
        if ((lastPage?.length || 0) < 10) return false;

        return allPages.length;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchIntervalInBackground: true,
      enabled: !!currentWorkspaceId,
    }
  );

  const items = data?.pages.flat() || [];

  useEffect(() => {
    if (items?.length > 0) {
      const notifications = items.map((not) => {
        return { ...not, buttonText: 'View details' };
      });

      notifications.sort((a, b) => {
        if (a.viewed === b.viewed) {
          return b.createdAt - a.createdAt;
        }
        return a.viewed ? 1 : -1;
      });

      changeGncNotifications(notifications);
    }
  }, [items.length]);

  const formatDateTime = (timestamp: number): string => {
    if (timestamp <= 0) return '';

    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hideNotification = (id: number) => {
    deleteNotificationApi(id, currentWorkspaceId).then(() => {
      queryClient.setQueryData(key, (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages
            .map((page: NotificationType[]) =>
              page.filter((notification: NotificationType) => notification.id !== id)
            )
            .filter((page) => page.length > 0),
        };
      });

      changeGncNotifications((prevState: GNCConfigItemType[]) => {
        return prevState.filter((notification) => notification.id !== id);
      });

      // Update notification counter if removing an unread notification
      queryClient.setQueryData(
        ['workspace-notifications-counter', currentWorkspaceId],
        (oldData: NotificationCounterType) => {
          if (!oldData) return oldData;
          // Only decrease counter if the removed notification was not viewed
          const notificationToRemove = gncNotifications.find(
            (notification) => notification.id === id
          );
          if (notificationToRemove && !notificationToRemove.viewed) {
            return { ...oldData, notViewedItems: Math.max(0, oldData.notViewedItems - 1) };
          }
          return oldData;
        }
      );
    });
  };

  const markAsViewed = (id: number) => {
    markNotificationAsSeenApi(id, currentWorkspaceId).then(() => {
      queryClient.setQueryData(key, (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: NotificationType[]) =>
            page.map((notification: NotificationType) =>
              notification.id === id
                ? { ...notification, viewed: true, viewedAt: new Date().toISOString() }
                : notification
            )
          ),
        };
      });

      changeGncNotifications((prevState: GNCConfigItemType[]) => {
        const updatedNotifications = prevState.map((notification) => {
          if (notification.id === id) {
            return { ...notification, viewed: true, viewedAt: new Date().getTime() };
          }
          return notification;
        });

        updatedNotifications.sort((a, b) => {
          if (a.viewed === b.viewed) {
            return b.createdAt - a.createdAt;
          }
          return a.viewed ? 1 : -1;
        });

        return updatedNotifications;
      });

      queryClient.setQueryData(
        ['workspace-notifications-counter', currentWorkspaceId],
        (oldData: NotificationCounterType) => {
          if (!oldData) return oldData;
          return { ...oldData, notViewedItems: Math.max(0, oldData.notViewedItems - 1) };
        }
      );
    });
  };

  const handleCloseSidebar = () => {
    dispatch(toggleNotificationsSidebar(false));
  };

  useOutsideClick(notificationsSidebarRef, handleCloseSidebar);
  useHotkeys({
    Escape: handleCloseSidebar,
  });

  if (!isNotificationsSidebarOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="notifications-sidebar" ref={notificationsSidebarRef}>
      <div className="notifications-sidebar__header">
        Notifications
        <div
          className="notifications-sidebar__close-icon"
          onClick={handleCloseSidebar}
          role="button"
          tabIndex={0}
        >
          <SVGIcon icon="crossBlack" />
        </div>
      </div>
      <div className="notifications-sidebar__body">
        {gncNotifications.length ? (
          gncNotifications.map(({ id, msg, link, buttonText, viewed, viewedAt, createdAt }) => {
            const handleClick = () => {
              if (!viewed) {
                markAsViewed(id);
              }

              if (link?.length > 0) {
                openNotificationLink(link);
                handleCloseSidebar();
              }
            };

            return (
              <div
                className={`notifications-sidebar__notification notifications-sidebar__notification--${viewed ? 'read' : 'unread'}`}
                key={id || msg}
              >
                <div
                  className="notifications-sidebar__close-notification"
                  onClick={(e) => {
                    e.stopPropagation();
                    hideNotification(id);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Hide notification"
                >
                  <SVGIcon icon="crossBlack" />
                </div>
                <div
                  className="notifications-sidebar__notification-content"
                  onClick={handleClick}
                  role="button"
                  tabIndex={0}
                >
                  <h4 className="notifications-sidebar__notification-title">{msg}</h4>
                  <div className="notifications-sidebar__notification-datetime">
                    {formatDateTime(createdAt)}
                  </div>
                  {buttonText && (
                    <div
                      className="notifications-sidebar__notification-text"
                      role="button"
                      tabIndex={0}
                    >
                      {buttonText}
                    </div>
                  )}
                  {viewedAt > 0 && (
                    <div className="notifications-sidebar__notification-datetime">
                      Viewed: {formatDateTime(viewedAt)}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="notifications-sidebar__no-notifications">No notifications to display</div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default NotificationsSidebar;
